'use client';

import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { VideoCameraIcon } from '@heroicons/react/24/solid';
import { bjToast } from '@components/common';
import { saveResumeInterviewAnswer } from '@services/interview/resumeInterviewApi';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { launchVideo, launchVoice } from '@components/features/interview-resolver/launchers';
import { PromptBarShell } from '@components/features/candidate-prompt-bar/PromptBarShell';
import { ChatBranch } from '@components/features/candidate-prompt-bar/ChatBranch';
import { MissionChipsRow } from '@components/features/candidate-prompt-bar/MissionChipsRow';
import { PromptBarActionsPopup } from '@components/features/candidate-prompt-bar/PromptBarActionsPopup';

interface StoriesAnswerPromptBarProps {
  /** The exact question of the currently-visible card (stored verbatim on text save). Absent
   *  when there's nothing to answer in context (e.g. viewer's own card) — the bar still shows
   *  with the same mic/video/submit controls, they just have nothing to save. */
  question?: string;
  locale: Locale;
  isAuthenticated?: boolean;
  /** Called after an answer (text saved / audio closed / video submitted) — refresh the gate. */
  onAnswered?: () => void;
  /** Opening line the voice agent speaks first — voice-websocket needs this to speak at all. */
  firstMessage?: string;
}

/**
 * The /stories bottom prompt bar in "answer" mode: the same bestie-chat bar (PromptBarShell +
 * ChatBranch + mission chips) wired to answer the currently-visible story card — text saves
 * directly, the mic answers by voice, the extra button answers by video. Not embedded in the card.
 * Always rendered on /stories now, even without a specific question (see `question` above).
 */
export const StoriesAnswerPromptBar = ({
  question, locale, isAuthenticated = false, onAnswered, firstMessage,
}: StoriesAnswerPromptBarProps) => {
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();
  const getQuestions = useCallback(() => Promise.resolve(question ? [question] : []), [question]);

  const handleSubmit = useCallback(async (text: string) => {
    if (!isAuthenticated) {
      void router.push('/login');
      return;
    }
    if (!question) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      await saveResumeInterviewAnswer(question, trimmed, locale);
      sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'text', subject: 'profile' });
      onAnswered?.();
    } catch {
      bjToast.error(t('audio-interview.job-landing.error'));
    }
  }, [question, isAuthenticated, router, locale, onAnswered, t]);

  const openVoice = useCallback(() => {
    if (!isAuthenticated) {
      void router.push('/login');
      return;
    }
    launchVoice({
      getQuestions,
      locale,
      kind: 'answer',
      firstMessage,
      onClosed: () => {
        sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'audio', subject: 'profile' });
        onAnswered?.();
      },
    });
  }, [isAuthenticated, router, getQuestions, locale, firstMessage, onAnswered]);

  const openVideo = useCallback(() => {
    if (!isAuthenticated) {
      void router.push('/login');
      return;
    }
    launchVideo({
      getQuestions,
      locale,
      onCompleted: () => {
        sendGoogleTagEvent('interview_answer', 'Custom', { channel: 'video', subject: 'profile' });
        onAnswered?.();
      },
    });
  }, [isAuthenticated, router, getQuestions, locale, onAnswered]);

  // Video-answer button, styled identically to ChatBranch's mic/send action button (white on
  // secondary) so the two controls read as one unified set.
  const videoButton = (
    <button
      type="button"
      onClick={openVideo}
      aria-label={t('ai.conversation.video', 'Video')}
      className="flex h-9! w-9! shrink-0 items-center justify-center rounded-lg! bg-secondary p-0! transition-transform hover:scale-110 hover:bg-secondary! lg:h-10! lg:w-10!"
    >
      <VideoCameraIcon className="size-5 text-white" />
    </button>
  );

  return (
    <PromptBarShell
      belowSlot={<MissionChipsRow locale={locale} mode="chat" isAuthenticated={isAuthenticated} className="mt-2" />}
    >
      <ChatBranch
        onSubmit={handleSubmit}
        onMic={openVoice}
        rightSlot={videoButton}
        placeholder={t('intercv_shout_hint')}
        leftSlot={<PromptBarActionsPopup locale={locale} mode="chat" isAuthenticated={isAuthenticated} />}
      />
    </PromptBarShell>
  );
};
